<template>
  <div class="cover"
       v-if="!doctor">
    <smd-loading-spinner/>
  </div>
  <smd-one-col-layout class="doctor-page"
                      content-styles="doctor-page__content-styles dark-light-bg"
                      v-else>
    <smd-header class="header" slot="header">
      <smd-btn icon="ns-icon-ico028"
               :bg="false"
               class="back-btn"
               slot="left-actions"
               @click="back"/>
      <span class="ns-icon-ico001"
            slot="right-actions"
            v-if="$isMobile">
        <span class="path1"></span>
        <span class="path2"></span>
      </span>
    </smd-header>
    <div v-if="doctor">

      <smd-alert-pane v-if="error" type="danger">
        <p>{{ error }}</p>
      </smd-alert-pane>

      <section class="doctor">
        <div class="image" :style="doctorImageStyle"></div>
        <h3 class="Title1">{{ doctor._fullName }}</h3>
        <p class="Title4">{{ doctor.mainspecialty }}</p>
      </section>

      <section class="patients">
        <h3>Patients</h3>

        <div v-for="patient in patients"
             :key="patient.id"
             class="patient-container smd-panel">
          <div v-if="patient.deleting" class="deleting-patient">
            <smd-loading-spinner/>
            <p class="fw-bold mt-xs">Deleting {{ patient.name }}</p>
          </div>
          <div v-if="!patient.deleting" class="patient">
            <smd-profile-img-placeholder v-if="!patient.profile_image"
                                         :text="patient.name"/>
            <smd-profile-img v-if="patient.profile_image"
                             :image="patient.profile_image"/>
            <p class="fw-bold ml-m">{{ patient.name }}</p>
            <button class="delete-patient-btn" @click="deletePatientConfirm(patient)">
              <i class="ns-icon-ico027"></i>
            </button>
          </div>
        </div>
      </section>

      <section class="hmos">
        <h3>HMOS</h3>

        <div v-for="hmo in hmos"
             :key="hmo.id"
             class="smd-panel hmo">
          <p class="hmo-code">{{ hmo.hmocode }}</p>
          <p class="available">Available Schedules</p>
          <div class="Body3">
            <strong class="Body5">On-site:</strong>
            {{ hmo.f2favailability | decimalToDays }}.
          </div>
          <p class="Body3">
            <strong class="Body5">Virtual:</strong>
            {{ hmo.virtualavailability | decimalToDays }}.
          </p>
        </div>
      </section>

      <section class="clinics">
        <h3>Clinics</h3>

        <div v-for="(clinic, clinicIndex) in clinics"
             :key="clinicIndex"
             class="smd-panel clinic">
          <i class="ns-icon-location"></i>
          <div class="details">
            <p class="name">{{ clinic.name }}</p>
            <p class="type">Available for <span>{{ clinic | clinicTypeAvailabilityText }}</span></p>
            <p v-if="clinic.fee" class="fee">Clinic fee: {{ clinic.fee | currency }}</p>

            <div class="schedules">
              <table>
                <tbody>
                <tr v-for="(schedule, scheduleIndex) in clinic.schedule"
                    :key="scheduleIndex">
                  <td class="schedules__day">{{ schedule.dayofweek | dayOfWeekIndexToWord }}</td>
                  <td>{{ schedule.starttime | formatScheduleTime }} - {{ schedule.endtime | formatScheduleTime }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  </smd-one-col-layout>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex';
  import {
    GETTER_TOKENS,
    GETTER_PATIENTS,
    GETTER_DOCTORS_BY_USER_TAG,
    ACTION_REMOVE_TOKEN,
  } from '@/store/types';
  import {
    DOCTORS_ROUTE_LIST,
    DOCTOR_ROUTE_PARAM,
    ERROR_ROUTE,
  } from '@/router/routes';
  import {
    decimalToDays,
    formatScheduleTime,
    dayOfWeekIndexToWord,
    clinicTypeAvailabilityText,
  } from '@/filters';
  import {AccountService, DELETE_PATIENT_RESULT} from '@/services/account';

  export default {
    name: 'doctor-page',
    filters: {
      decimalToDays,
      formatScheduleTime,
      dayOfWeekIndexToWord,
      clinicTypeAvailabilityText,
    },
    data() {
      return {
        error: null,
        doctor: null,
        patients: [],
      };
    },
    computed: {
      ...mapGetters({
        tokens: GETTER_TOKENS,
        storePatients: GETTER_PATIENTS,
        doctorsListByUserTag: GETTER_DOCTORS_BY_USER_TAG,
      }),
      doctorImageStyle() {
        return {
          background: `url(${this.doctor.profile_url}) no-repeat center`,
          backgroundSize: 'cover',
        };
      },
      clinics() {
        if (!this.doctor) {
          return [];
        }

        return this.doctor.clinics
          .filter(({onsite, virtual, visible}) => {
            if (!visible) {
              return false;
            }

            return onsite || virtual;
          });
      },
      hmos() {
        if (!this.doctor) {
          return [];
        }

        const hmos = [];

        for (let hmoKey in this.doctor.hmos) {
          const hmo = this.doctor.hmos[hmoKey];
          const {f2favailability, virtualavailability} = hmo.schedule
            .filter(({clinicid}) => {
              return !!this.clinics.find((clinic) => clinic.clinicid === clinicid);
            })
            .reduce((mergedSchedule, schedule) => {
              const matchedDoctorClinic = this.clinics.find(({clinicid}) => schedule.clinicid === clinicid);
              const clinicDays = matchedDoctorClinic.schedule
                .map(({dayofweek}) => dayofweek)
                .reduce((availableDays, day) => {
                  let dayInBinary = '0000000'.split('');
                  dayInBinary[dayInBinary.length - (day + 1)] = 1;
                  return availableDays | parseInt(dayInBinary.join(''), 2);
                }, 0);
              const matchedF2fHmoScheduleToClinicDays = matchedDoctorClinic.onsite
                ? schedule.f2favailability & clinicDays
                : 0;
              const matchedVirtualHmoScheduleToClinicDays = matchedDoctorClinic.virtual
                ? schedule.virtualavailability & clinicDays
                : 0;

              return {
                f2favailability: mergedSchedule.f2favailability | matchedF2fHmoScheduleToClinicDays,
                virtualavailability: mergedSchedule.virtualavailability | matchedVirtualHmoScheduleToClinicDays,
              };
            }, {f2favailability: 0, virtualavailability: 0});

          if (f2favailability || virtualavailability) {
            hmos.push({
              hmocode: hmo.hmocode,
              f2favailability,
              virtualavailability,
            });
          }
        }

        return hmos;
      },
    },
    watch: {
      doctorsListByUserTag: {
        handler(newValue) {
          if (newValue) {
            this.setInitialValues();
          }
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions({
        removeTokens: ACTION_REMOVE_TOKEN,
      }),
      back() {
        this.$router.push({name: DOCTORS_ROUTE_LIST});
      },
      setInitialValues() {
        this.doctor = this.doctorsListByUserTag[this.$route.params[DOCTOR_ROUTE_PARAM]];

        if (!this.doctor) {
          this.$router.push({name: ERROR_ROUTE});
        }

        this.patients = Object.keys(this.storePatients)
          .filter((patientId) => {
            return this.storePatients[patientId].doctor.usertag === this.$route.params[DOCTOR_ROUTE_PARAM];
          })
          .map((patientId) => ({
            ...this.storePatients[patientId],
            // `deleting` flag is use to indicate loading if the patient is being deleted
            deleting: false,
          }));
      },
      async deletePatientConfirm(patient) {
        const removePatient = await this.$alert.show({
          text: `Do you want to remove patient ${patient.name}?`,
          buttons: [
            {value: true, label: 'Yes'},
            {value: false, label: 'No'},
          ],
        });

        if (removePatient) {
          this.deletePatient(patient);
        }
      },
      async deletePatient(patient) {
        const {token} = this.tokens.find(({patient: {id}}) => {
          return id === patient.id;
        });

        if (token) {
          this.error = null;
          patient.deleting = true;

          try {
            const {data: {result}} = await AccountService.removePatientToken(token);

            if (result === DELETE_PATIENT_RESULT.SUCCESS) {
              this.removeTokens([token]);
            } else if (result === DELETE_PATIENT_RESULT.NOT_EXISTS) {
              patient.deleting = false;
              this.error = 'Failed to remove patient. Patient does not exists.';
            } else {
              patient.deleting = false;
              this.error = 'Failed to remove patient.';
            }
          } catch (error) {
            patient.deleting = false;
            this.error = 'An unexpected error occured on removing patient.';
          }
        } else {
          this.error = 'An unexpected error occured on removing patient. Please try to refresh page.';
        }
      },
    },
  }
</script>

<style lang="scss">
  .doctor-page {

    .smd-alert-pane {
      margin-bottom: space(m);
    }

    .patient-container {
      border-radius: radius(medium);
      background: #F7F8FA;
      padding: space(m);

      &:not(:last-child) {
        margin-bottom: space(m);
      }
    }

    .deleting-patient {
      width: 100%;
      text-align: center;
    }

    section {
      margin-bottom: space(m);

      h3 {
        @include font-style(Title1);
        margin-bottom: space(m);
      }
    }

    .doctor {
      margin-bottom: space(xl);

      .image {
        width: 128px;
        height: 128px;
        border-radius: 50%;
        margin: 0 auto space(m) auto;
      }

      h3, p {
        text-align: center;
        margin-bottom: 0;
      }
    }

    .patient {
      display: flex;
      align-items: center;
      justify-content: center;

      p {
        flex: 1;
      }

      .delete-patient-btn {
        @extend %layout--flex-center-xy;
        background: color(red, background);
        width: 20px;
        height: 20px;
        min-width: unset;
        min-height: unset;
        padding: 0;
        position: relative;
        top: -20px;

        i {
          font-size: 15px;
          font-weight: 700;

          &:before {
            color: color(white, text);
          }
        }
      }
    }

    .hmo {
      border-radius: radius(medium);
      padding: space(m);

      &:not(:last-child) {
        margin-bottom: space(m);
      }

      p {
        &.hmo-code {
          @include font-style(Title3);
          margin-bottom: space(s);
        }

        &.available {
          @include font-style(Body5);
          color: color(gray-400);
          margin-bottom: space(xs);
        }
      }
    }

    .clinic {
      display: flex;
      padding: space(m);
      border-radius: radius(medium);

      &:not(:last-child) {
        margin-bottom: space(m);
      }

      i {
        color: color(yellowGreen, background);
        font-size: font(xl);
        margin-right: space(m);
      }

      .details {
        flex: 1;

        .name {
          @include font-style(Title3);
          margin-bottom: space(xs);
        }

        .type {
          @include font-style(Body3);
          color: color(gray, text);

          span {
            @include font-style(Body5);
            color: color(label, text);
          }
        }

        .fee {
          @include font-style(Body3);
          color: color(label, text);
        }
      }

      .schedules {
        @include font-style(Body3);
        margin-top: space(s);

        table {
          border-spacing: 0;

          tr:not(:last-child) {
            margin-bottom: space(s);
          }

          td {
            padding-bottom: space(xs);

            &:first-child {
              min-width: 104px;
              font-weight: font(medium);
              padding-right: space(m);
            }
          }
        }
      }

      .schedules__day {
        @include font-style(Body5);
      }
    }
  }

  .doctor-page__content-styles {
    @include responsive('min-width: 1024px') {
      padding: space(xl) !important;
    }

    > div {
      @include set-max-width($max-width: 900px);
    }
  }
</style>
